import type { ProductId, QuizConfig, TerpeneId } from "../types";

const terpeneAnswer = (id: string, label: string, terpene: TerpeneId, weight = 1) => ({
  id,
  label,
  terpene,
  weight,
} as const);

const productAnswer = (id: string, label: string, product: ProductId) => ({
  id,
  label,
  product,
} as const);

export let quizConfig: QuizConfig = {
  version: "terpene-personality-v1",
  tieBreakerQuestionIds: ["q3", "q1", "q2", "q4", "q5"],
  questions: [
    {
      id: "q1",
      prompt: "What kind of energy are you most drawn to right now?",
      type: "terpene",
      answers: [
        terpeneAnswer("q1-myrcene", "Deep, slow and grounded", "myrcene"),
        terpeneAnswer("q1-caryophyllene", "Steady, warm and resilient", "caryophyllene"),
        terpeneAnswer("q1-limonene", "Bright, fresh and optimistic", "limonene"),
        terpeneAnswer("q1-linalool", "Soft, calm and spacious", "linalool"),
      ],
    },
    {
      id: "q2",
      prompt: "Your ideal reset after a big day is…",
      type: "terpene",
      answers: [
        terpeneAnswer("q2-myrcene", "Putting everything on hold and fully switching off", "myrcene"),
        terpeneAnswer("q2-caryophyllene", "A familiar ritual that brings me back to centre", "caryophyllene"),
        terpeneAnswer("q2-limonene", "Fresh air, sunlight or a change of scene", "limonene"),
        terpeneAnswer("q2-linalool", "Quiet, soft lighting and something soothing", "linalool"),
      ],
    },
    {
      id: "q3",
      prompt: "Which natural aroma do you instinctively choose?",
      type: "terpene",
      answers: [
        terpeneAnswer("q3-myrcene", "Ripe mango or fresh hops", "myrcene", 2),
        terpeneAnswer("q3-caryophyllene", "Black pepper or cloves", "caryophyllene", 2),
        terpeneAnswer("q3-limonene", "Orange or lemon peel", "limonene", 2),
        terpeneAnswer("q3-linalool", "Lavender", "linalool", 2),
      ],
    },
    {
      id: "q4",
      prompt: "When life feels noisy, what helps you return to yourself?",
      type: "terpene",
      answers: [
        terpeneAnswer("q4-myrcene", "Slowing the pace", "myrcene"),
        terpeneAnswer("q4-caryophyllene", "Grounding myself and holding my boundaries", "caryophyllene"),
        terpeneAnswer("q4-limonene", "Finding something hopeful or energising", "limonene"),
        terpeneAnswer("q4-linalool", "Creating calm and gentleness", "linalool"),
      ],
    },
    {
      id: "q5",
      prompt: "Which setting feels most like you?",
      type: "terpene",
      answers: [
        terpeneAnswer("q5-myrcene", "A slow Sunday with nowhere to be", "myrcene"),
        terpeneAnswer("q5-caryophyllene", "A warm kitchen, garden or familiar place", "caryophyllene"),
        terpeneAnswer("q5-limonene", "Open windows, sunshine and movement", "limonene"),
        terpeneAnswer("q5-linalool", "Bath, books, blankets and low light", "linalool"),
      ],
    },
    {
      id: "q6",
      prompt: "What do you need most right now?",
      type: "product",
      answers: [
        productAnswer("q6-calm", "Calm through busy days", "calm"),
        productAnswer("q6-sleep", "Support for a bedtime wind-down and restful sleep", "sleep"),
        productAnswer("q6-both", "Both: calmer days and restful nights", "both"),
      ],
    },
  ],
  results: {
    myrcene: {
      id: "myrcene",
      name: "Myrcene",
      title: "The Grounder",
      sourceLabel: "Mango, hops and green herbs",
      description: "Earthy, green and softly musky, Myrcene carries the familiar scent of ripe mango, fresh hops and crushed herbs. Its deep, unhurried character suits moments that call for less noise, more comfort and space to settle into your own rhythm.",
      personalityLines: [
        "You feel most like yourself when life is allowed to slow down.",
        "You’re drawn to familiar comforts, unhurried moments and room to exhale.",
        "When everything feels too full, you instinctively look for somewhere softer to land.",
      ],
      foundIn: ["Ripe mango", "Hops", "Lemongrass", "Bay leaves", "Verbena", "Citrus fruit and juice", "Cannabis cultivars"],
      sourceImage: "/assets/source-myrcene.webp",
      shareCard: "/share/myrcene.png",
    },
    caryophyllene: {
      id: "caryophyllene",
      name: "Caryophyllene",
      title: "The Anchor",
      sourceLabel: "Black pepper, cloves and warm spices",
      description: "Warm, woody and unmistakably peppery, Caryophyllene brings to mind cracked black pepper, cloves and a kitchen rich with spice. Its steady character feels at home in grounding rituals, clear boundaries and the reassuring strength of what is known and trusted.",
      personalityLines: [
        "You have a steady presence that people often feel before you say a word.",
        "Familiar rituals and trusted people help bring you back to centre.",
        "You value warmth, loyalty and the quiet confidence of knowing where you stand.",
      ],
      foundIn: ["Black pepper", "Cloves", "Cinnamon", "Basil", "Oregano", "Rosemary", "Hops", "Cannabis cultivars"],
      sourceImage: "/assets/source-caryophyllene.webp",
      shareCard: "/share/caryophyllene.png",
    },
    limonene: {
      id: "limonene",
      name: "Limonene",
      title: "The Brightener",
      sourceLabel: "Citrus peel and fresh green botanicals",
      description: "Bright, zesty and immediately recognisable, Limonene carries the sparkling scent released when citrus peel is twisted between your fingers. Its lively aromatic character suits fresh perspectives, open windows and the feeling that a new direction is possible.",
      personalityLines: [
        "You naturally look for the opening, the possibility or the brighter angle.",
        "A change of scene can quickly help you feel like yourself again.",
        "You bring lightness without ignoring what is real—you simply prefer to keep moving.",
      ],
      foundIn: ["Orange peel", "Lemon peel", "Grapefruit", "Mandarin", "Juniper", "Fennel", "Dill", "Cannabis cultivars"],
      sourceImage: "/assets/source-limonene.webp",
      shareCard: "/share/limonene.png",
    },
    linalool: {
      id: "linalool",
      name: "Linalool",
      title: "The Softener",
      sourceLabel: "Lavender, flowers and aromatic herbs",
      description: "Floral, delicate and gently herbaceous, Linalool is familiar from lavender, coriander and fragrant garden flowers. Its soft aromatic character belongs to quiet rooms, low light and the small rituals that create space around an overstimulated mind.",
      personalityLines: [
        "You notice subtle shifts in atmosphere and feel deeply affected by your surroundings.",
        "Gentleness is not an escape for you—it is how you return to yourself.",
        "You’re drawn to quiet beauty, thoughtful details and people who make softness feel safe.",
      ],
      foundIn: ["Lavender", "Coriander", "Basil", "Bergamot", "Jasmine", "Rosewood", "Cinnamon and cardamom", "Cannabis cultivars"],
      sourceImage: "/assets/source-linalool.webp",
      shareCard: "/share/linalool.png",
    },
  },
  products: {
    calm: {
      id: "calm",
      name: "Calm",
      description: "For daytime balance and a calmer rhythm through busy days.",
      affiliateUrl: "https://collabs.shop/7uxmiv",
      imageView: "calm",
    },
    sleep: {
      id: "sleep",
      name: "Sleep",
      description: "For an evening wind-down and support for restful sleep.",
      affiliateUrl: "https://collabs.shop/lsqaki",
      imageView: "sleep",
    },
    both: {
      id: "both",
      name: "Calm + Sleep",
      description: "A paired rhythm for busy days and your evening wind-down.",
      affiliateUrl: "https://collabs.shop/lujrf7",
      imageView: "both",
    },
  },
};

export function setQuizConfig(config: QuizConfig): void {
  quizConfig = config;
}
